import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { MatSort, Sort } from '@angular/material/sort';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { PriceAsService } from 'src/app/modules/marketing/products/services/price-as-service/price-as.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { CountryCode, countriesWithoutConsignedWarehouse, oemSkuField } from 'core/app-info/country-specifics.enum';
import { AppStateService } from 'core/app-state/app-state.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ModalComponent } from 'core/dialog/modal/modal.component';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { TooltipNotificationComponent } from 'core/tooltip-notification/tooltip-notification.component';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SignalRUpload } from 'src/app/modules/shared/entities/signalr-upload.class';
import { ImportItemsQuoteService } from 'src/app/modules/shared/import-quotes-and-orders/import-items-quote.service';
import { SignalrConnectionType } from '../../../shared/entities/signalrConnectionType.enum';
import { SignalrMethodName } from '../../../shared/entities/signalrMethodName.enum';
import { SignalrNotification } from '../../../shared/entities/SignalrNotification.class';
import { SignalrProgressState } from '../../../shared/entities/signalrProgressState.enum';
import { ConsignedWarehouseComponent } from '../consigned-warehouse/consigned-warehouse.component';
import { CustomToolComponent } from '../custom-tool/custom-tool.component';
import { Product } from '../entities/products.class';
import { SortColumn, SortDirection } from '../entities/sort.enum';
import { QuoteAtpComponent } from '../quote-atp/quote-atp.component';
import { ConsignedWarehouseService } from '../services/consigned-warehouse.service';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';
import { ToolNotesDialogComponent } from '../tool-notes-dialog/tool-notes-dialog.component';
import { ProductNotificationEvent } from '../entities/product-notification-event';
import { AddProductComponent } from '../add-item/add-product.component';

@Component({
  selector: 'app-product-grid',
  templateUrl: './product-grid.component.html',
  styleUrls: ['./product-grid.component.scss'],
  standalone: false
})
export class ProductGridComponent implements OnInit, OnChanges, OnDestroy {
  private lastRowSelector = '#productListTable .mat-row:last-child';
  private partNumberInputId = 'partNumberNewInput';
  private editableRow: Product;
  private oldRowData: Product;
  private subscriptions = new Subscription();
  private customerId: string = null;
  private leadId: string = null;
  private isSavingRow = false;
  isLead = false;
  isMobile: boolean;
  currentPage = 0;
  appStrings: AppLocalization;
  isLoading = false;
  addProductInProgress = false;
  isUploading = false;
  menuItems = [];
  recordList: Subject<Product[]>;
  private currentProductList: Product[];
  totalRecords = 0;
  totalPages = 0;
  pageSize = this.quoteToolsService.pageSize;
  displayedColumns = ['lineNumber', 'customTool', 'partNumber', 'oemSku', 'description', 'quantity',
    'countryOfOrigin', 'listPrice', 'netPrice', 'totalPrice', 'atp', 'lineWeight', 'notifications'];
  private templateValues;
  quoteDetails;
  columnToUpdate = false;
  checkboxForm: FormGroup;
  minSortCount = 2;
  notificationDialogObj = {
    tool: {},
    index: 0
  };
  disableExcelUpload = false;
  disableImport = false;
  disableConsignedWarehouseMenu = false;
  cwLoaded = this.consignedWarehouseService.cwLoaded;
  warnForErrors = false;
  startItemEditInProgress = false;
  weightSymbol = this.appInfoService.weightSymbol;
  private fitContentSize = 'fit-content';
  hasWarehouses: boolean;
  itemizeInProgress: boolean;
  oemSkuColumn = 'oemSku';
  atpColumn = 'atp';
  quoteSaveInProgress = false;
  private panelClass = 'full-width-dialog';
  isOnline: boolean;
  electronAppOutOfSync = false;
  consignedDisabledBasedOnCountry = false;
  currentRow: FormGroup;
  newProductRow: FormGroup;
  dialogRef: MatDialogRef<ModalComponent>;
  dialogOpen$ = new EventEmitter<boolean>();
  dialogButtons: any;
  dialogTitle: string;
  dialogText: any;
  dialogDisableClose: boolean;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(TooltipNotificationComponent) tooltipNotificationComponent: TooltipNotificationComponent;
  @ViewChild('fileUploadControl') fileUploadControl: ElementRef;
  @Output() goToPaymentDetails = new EventEmitter();
  @Output() goToOrderNotifications = new EventEmitter();
  @Input() errorCount: number;
  @Input() canEdit: boolean;
  @Input() currencySymbol: string;
  countryCode: string;
  countryCodeId: number;
  displayCheckboxOEMsku: boolean;
  quoteNumber: string;
  dateFormat: string;
  private clickOutsideHandler: (event: Event) => void;

  constructor(
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private consignedWarehouseService: ConsignedWarehouseService,
    private quoteDetailsService: QuoteDetailsService,
    private signalrService: SignalRService,
    private quoteToolsService: QuoteToolsService,
    private router: Router,
    private fb: FormBuilder,
    private priceAsService: PriceAsService,
    private deviceService: DeviceDetectorService,
    private cdRef: ChangeDetectorRef,
    private importItemsQuoteService: ImportItemsQuoteService
  ) { }

  ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.isOnline = this.appStateService.state.isOnline;
    this.dateFormat = this.appInfoService.dateFormat;
    this.electronAppOutOfSync = this.appStateService.checkIfElectronAppIsOutOfSync();

    this.currentRow = this.fb.group({
      lineNumber: [],
      customTool: [],
      productNumber: [],
      description: [],
      quantity: [],
      netPrice: [],
    });

    this.newProductRow = this.fb.group({
      productNumber: [''],
      description: [''],
      quantity: [1],
      netPrice: [''],
      notes: [''],
    });

    this.updateFormState();
    this.initData();
    this.subscriptions.add(
      this.route.paramMap.subscribe(() => {
        this.initDataForQuote();
      })
    );
    this.subscriptions.add(this.quoteToolsService.itemizeInProgress$.subscribe(
      actionInProgress => this.itemizeInProgress = actionInProgress));
    this.subscriptions.add(this.quoteDetailsService.quoteIsSaving$.subscribe(progress => this.quoteSaveInProgress = progress));
    this.subscriptions.add(this.quoteDetailsService.quoteNumber$.subscribe(quoteNumber => this.quoteNumber = quoteNumber));

    if (!this.isMobile) {
      this.setupClickOutsideListener();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.canEdit && this.newProductRow) {
      this.updateFormState();
      this.setMenuItems();
    }
  }

  roundWeight(weight: number) {
    return (Math.floor(weight * 100) / 100).toFixed(2);
  }

  private initData() {
    this.countryCode = this.appInfoService.userData?.UserInfo?.CountryCode || CountryCode.US;
    this.consignedDisabledBasedOnCountry = countriesWithoutConsignedWarehouse[this.countryCode] || false;
    this.displayCheckboxOEMsku = oemSkuField[this.countryCode];
    this.appStrings = this.appInfoService.appStrings;
    this.recordList = this.quoteToolsService.productList$;

    this.checkboxForm = this.fb.group({
      showCustomTool: [false],
      showOemSku: [false],
      atpChecked: [false]
    });

    this.subscriptions.add(this.quoteDetailsService.setCustomer$.subscribe(customer => {
      this.isLead = customer.isLead;
      this.customerId = this.isLead ? null : customer.id;
      this.leadId = this.isLead ? customer.id : null;
      this.countryCodeId = customer.countryCodeId;
    }));

    this.isLoading = true;

    this.subscriptions.add(
      this.quoteToolsService.productCount$.subscribe(recordCount => {
        this.totalRecords = recordCount;
        this.totalPages = Math.ceil(recordCount / this.pageSize);
        this.isLoading = false;
      }));
    this.subscriptions.add(
      this.recordList.subscribe(productList => {
        this.currentProductList = productList;
        this.consignedWarehouseService.cwLoaded.productList = true;
        if (productList) {
          this.updateAddMultipleItemsActions(productList);
        }
      }));
    this.subscriptions.add(this.quoteToolsService.pageIndex$.subscribe(pageIndex => this.currentPage = pageIndex));
    this.subscriptions.add(this.quoteToolsService.refreshToolsList$.subscribe((callback: () => void) => {
      const { sortColumn, sortDirection } = this.getSortParams();
      this.quoteToolsService.getProductList(this.currentPage, sortColumn, sortDirection, callback);
    }));

    this.quoteToolsService.getStaticValues(staticValues => this.initializeValues(staticValues));
    this.subscriptions.add(this.consignedWarehouseService.warehouseList$.subscribe((warehouseList: any[]) =>
      this.hasWarehouses = Boolean(warehouseList.length)
    ));
    this.subscriptions.add(this.signalrService.saveUploadedProductsReceived$.subscribe((saveUploadedProductsProgress: SignalRUpload) =>
      this.setUploadedProductsProgress(saveUploadedProductsProgress)
    ));
    this.subscriptions.add(this.signalrService.signalrError$.subscribe(() => this.isUploading = false));
    this.subscriptions.add(this.quoteToolsService.focusPartNumberInput$.subscribe(value => {
      if (value) {
        this.focusPartNumberInput();
      }
    }));
    this.subscriptions.add(this.quoteToolsService.addPriceAsTool$.subscribe(tool => {
      const product = new Product();
      product.productNumber = tool.productNumber;
      product.quantity = tool.quantity;
      this.addNewTool(product);
      this.priceAsService.clearQuoteData();
    }));
    this.subscriptions.add(this.quoteToolsService.showOemSku$.subscribe(showOemSku => {
      this.checkboxForm.controls.showOemSku.setValue(showOemSku);
    }));
    this.subscriptions.add(this.quoteToolsService.atpChecked$.subscribe(atpChecked => {
      this.checkboxForm.controls.atpChecked.setValue(atpChecked);
    }));
    this.setMenuItems();
  }

  private initDataForQuote() {
    const quoteId = this.route.snapshot.paramMap.get('quoteId');
    this.quoteToolsService.setQuoteId(quoteId);
    let cb: () => void;
    if (quoteId && this.priceAsService.quoteInfo?.product && this.priceAsService.quoteInfo.quoteId === quoteId) {
      // Product should be added to the quote via Price As -> Add To Quote functionality
      cb = () => this.quoteToolsService.addPriceAsTool$.next(this.priceAsService.quoteInfo.product);
    }
    this.quoteToolsService.getProductList(this.currentPage, null, null, cb);
  }

  initializeValues(staticValues) {
    this.templateValues = staticValues[0];
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.removeClickOutsideListener();
  }

  /**
   * Sets up click outside listener
   */
  private setupClickOutsideListener() {
    this.clickOutsideHandler = (event: Event) => {
      if (!this.editableRow) {
        return;
      }

      const target = event.target as HTMLElement;
      const tableElement = document.getElementById('productListTable');
      if (tableElement && !tableElement.contains(target)) {
        this.saveEditedRow();
      }
    };
    document.addEventListener('click', this.clickOutsideHandler, true);
  }

  /**
   * Removes click outside listener
   */
  private removeClickOutsideListener() {
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler, true);
    }
  }

  /**
   * Updates the disableExcelUpload, disableConsignedWarehouseMenu and disableImport properties based on the product data
   * the productList will always have an empty product template on the first position.
   * Currently there can only be one add multiple products version : excel OR consigned.
   * If the product has a non-null value in warehouseCode, then disable excel.
   */
  private updateAddMultipleItemsActions(productList: Product[]) {
    if (!productList.length || productList.length === 1) {
      this.disableConsignedWarehouseMenu = false;
      this.disableExcelUpload = false;
      this.disableImport = false;
    } else if (productList[1].consignedWarehouse) {
      this.disableConsignedWarehouseMenu = false;
      this.disableExcelUpload = true;
      this.disableImport = true;
    } else {
      this.disableConsignedWarehouseMenu = true;
      this.disableExcelUpload = false;
      this.disableImport = false;
    }
  }

  private updateFormState() {
    if (!this.canEdit || this.quoteSaveInProgress) {
      this.newProductRow.disable();
    } else {
      this.newProductRow.enable();
    }
  }

  /**
   * Checks if index is zero
   * @param index - The index
   */
  isIndexZero(index: number) {
    return index === 0;
  }

  /**
   * Checks if the row is the new product row (index 0)
   * @param index - The row index
   */
  isNewProductRow(index: number): boolean {
    return index === 0;
  }

  /**
   * Checks if the row is an existing product row (index > 0)
   * @param index - The row index
   */
  isExistingProductRow(index: number): boolean {
    return index > 0;
  }

  /**
   * Toggles the current value of the showOemSku flag
   */
  toggleOemSku() {
    this.quoteToolsService.updateOemSkuFlag(this.checkboxForm.controls.showOemSku.value);
  }

  setMenuItems() {
    const getConsignedDescription = () => {
      if (this.disableConsignedWarehouseMenu) {
        return this.appStrings.label_consignedWhNotAvailable;
      } else if (!this.hasWarehouses) {
        return this.appStrings.label_consignedWhEmpty;
      }
    };

    const importMenuItems = [{
      value: this.appStrings.button_downloadExcel,
      icon: 'file_download',
      isDisabled: !this.canEdit,
    }, {
      value: this.appStrings.button_uploadExcel,
      description: this.disableExcelUpload ? this.appStrings.button_uploadExcel : '',
      isDisabled: !this.canEdit || this.disableExcelUpload,
      icon: 'insert_drive_file',
    }];

    if (!this.isLead && !this.consignedDisabledBasedOnCountry) {
      importMenuItems.push({
        value: this.appStrings.button_consignedWarehouse,
        description: getConsignedDescription(),
        isDisabled: !this.canEdit || this.disableConsignedWarehouseMenu || !this.hasWarehouses,
        icon: 'warehouse',
      });
    }

    if (!this.isMobile) {
      importMenuItems.push({
        value: this.appStrings.button_importQuotesOrOrders,
        description: this.disableImport ? this.appStrings.label_disableUploadExcel : '',
        isDisabled: !this.canEdit || this.disableImport,
        icon: 'description',
      });
    }

    this.menuItems = importMenuItems;
    this.cdRef.detectChanges();
  }

  onMenuClick($event: any) {
    if ($event === this.appStrings.button_downloadExcel) {
      this.downloadExcelTemplate();
    } else if ($event === this.appStrings.button_uploadExcel && !this.disableExcelUpload) {
      this.triggerUpload();
    } else if ($event === this.appStrings.button_consignedWarehouse && !this.disableConsignedWarehouseMenu && this.hasWarehouses) {
      this.showConsignedWarehousePopup();
    } else if ($event === this.appStrings.button_importQuotesOrOrders && !this.disableImport) {
      this.copyQuoteDetailsForImport();
    }
  }

  /**
   * Opens uncheck custom tool dialog when custom tool not checked
   * @param element tool
   */
  customToolNotCheckedPopup(element: Product) {
    const confirm = () => this.confirmRemovalCustomFlag(element);
    const cancel = () => {
      element.customTool = true;
      this.quoteToolsService.refreshToolsList$.next(undefined);
    };

    this.dialogTitle = this.appStrings.quotes_uncheckCustomToolTitle;
    this.dialogText = this.appStrings.quotes_uncheckCustomTool;
    this.dialogDisableClose = true;
    this.dialogButtons = [
      { label: 'Confirm', type: 'primary', onclick: confirm.bind(this) },
      { label: 'Cancel', type: 'secondary', onclick: cancel.bind(this) },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }

  /**
   * Confirming removal of custom flag
   * @param element tool
   */
  confirmRemovalCustomFlag(element: Product) {
    element.customTool = false;
    this.quoteToolsService.saveCustomToolInfo({
      productId: element.id,
      description: element.description || '',
      countryOfOrigin: element.countryId || 0,
      listPrice: element.listPrice || 0.01,
      netPrice: element.netPrice || 0.01,
      weight: element.weight || 0.01,
      isRemoved: true
    }, element);
  }

  /**
   * Opens uncheck custom tool dialog when custom tool is checked
   * @param element tool
   * @param fromNotificationIcon is opened from notification icon
   */
  customToolCheckedPopup(element, fromNotificationIcon) {
    const dialogRef = this.dialog.open(CustomToolComponent, {
      panelClass: this.isMobile ? ['ds', 'fullWidth', 'fullScreen'] : ['ds', 'fullWidth'],
      data: {
        productInfo: element
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 0) {
        if (!fromNotificationIcon) {
          element.customTool = false;
        }
        element.updateCustomToolInProgress = false;
        this.quoteToolsService.refreshToolsList$.next(undefined);
      }

      // If the custom tool is added from the notification
      if (result === 1) {
        element.customTool = true;
      }
    });
  }

  /**
   * Loads a specific page
   * @param event - The page event
   */
  loadPage(event: PaginationPageEvent) {
    this.currentPage = event.pageNumber - 1;
    const { sortColumn, sortDirection } = this.getSortParams();
    this.quoteToolsService.getProductList(this.currentPage, sortColumn, sortDirection);
  }

  /**
   * Set default quantity to 1
   * @param newTool - The new tool
   */
  setDefaultQuantity(newTool: Product) {
    if (!newTool.quantity) {
      newTool.quantity = 1;
    }
  }

  /**
   * Adds a new tool
   * @param tool - The new tool row
   */
  addNewTool(tool: Product) {
    tool.netPrice = this.modifyNetPrice(tool.netPrice);

    if (tool.productNumber && tool.productNumber.indexOf('%') === -1) {
      tool.calculateTotals = !this.isLead;
      this.isLoading = true;
      this.addProductInProgress = true;
      this.disableConsignedWarehouseMenu = true;

      if (!tool.netPrice) {
        this.addToolWithNoNetPrice(tool);
      } else {
        // display net price change pop-up before adding the product in db
        this.netPriceHasChanged((result) => {
          if (result === 0) {
            // user responded yes to pop-up with confirmation
            this.netPriceChangeConfirmed(tool);
          } else if (result === 1) {
            this.netPriceChangeRejected(tool);
          }

        });
      }
    } else {
      this.notificationService.showNotification(this.appStrings.products_gridInvalidPartNumber, NotificationType.Error, true);
    }
  }

  /**
   * Adding a tool with empty net price
   * @param tool added tool
   */
  addToolWithNoNetPrice(tool: Product) {
    delete tool.netPrice;
    // don't display net price pop-up if the user doesn't enter the price when adding a new tool
    this.quoteToolsService.addTool(tool)
      .subscribe((newToolData: Product) => {
        this.isLoading = false;
        this.addProductInProgress = false;
        this.quoteToolsService.processAdd(tool, newToolData, this.currentPage, () => {
          if (this.checkboxForm.controls.atpChecked && this.isOnline) {
            this.quoteToolsService.refreshProductAtp(newToolData.id, () => this.afterAdd());
          } else {
            this.afterAdd();
          }
        });
      }, error => {
        this.quoteToolsService.toolEditInProgress$.next(false);
        this.afterAdd(error);
      });
  }

  /**
   * Adding tool after net price change rejected
   * @param tool product
   */
  netPriceChangeRejected(tool: Product) {
    delete tool.netPrice;
    this.quoteToolsService.addTool(tool)
      .subscribe((newToolData: Product) => {
        this.isLoading = false;
        this.addProductInProgress = false;
        this.quoteToolsService.processAdd(tool, newToolData, this.currentPage, () => this.afterAdd());
      });
  }

  /**
   * Adding tool after net price change confirmed
   * @param tool added product
   */
  netPriceChangeConfirmed(tool: Product) {
    // adding tool in db
    this.quoteToolsService.addTool(tool)
      .subscribe((newToolData: Product) => {
        this.isLoading = false;
        this.addProductInProgress = false;
        this.quoteToolsService.processAdd(tool, newToolData, this.currentPage, () => this.afterAdd());
        // if the user adds a different price than the list price
        // notifying backend of net price change
        if (newToolData.dbNetPrice !== newToolData.netPrice && (newToolData.dbNetPrice >= 0) && this.countryCode === CountryCode.US) {
          this.quoteToolsService.sendNetPriceChanges(
            this.quoteNumber, newToolData.dbNetPrice, newToolData.netPrice, newToolData.productNumber).subscribe();
        }
      }, error => {
        this.quoteToolsService.toolEditInProgress$.next(false);
        this.afterAdd(error);
      });
  }

  /**
   * Modifying net price
   * @param netPrice net price
   * @returns formatted net price as number
   */
  modifyNetPrice(netPrice: number) {
    if (Math.floor(netPrice).toString().length > 8) {
      netPrice = Math.floor(netPrice / 10) + Number(netPrice?.toString().split('.')[1]) / 100;
    }

    return Number(netPrice);
  }

  /**
   * Resets the inputs and shows the newly added tool if needed
   */
  afterAdd(error?: any) {
    this.isLoading = false;
    this.addProductInProgress = false;
    this.quoteToolsService.productListHasChanges$.next(true);
    this.quoteToolsService.hasProductsFromConsigned$.next(false);
    if (error) {
      return;
    }

    if (!this.isMobile) {
      // reset sort
      if (this.sort.active || this.sort.direction) {
        this.sort.sort({ id: '', start: 'asc', disableClear: false });
        this.sortData({ active: '', direction: '' });
      }

      /**
       * Showcase the added element
       */
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      setTimeout(() => {
        const selectedRow = document.querySelector(this.lastRowSelector);
        if (selectedRow) {
          selectedRow.classList.add('pulse');
        }
      }, 300);
      this.focusPartNumberInput();
    }
  }

  /**
   * Focuses the New Part Number input
   */
  focusPartNumberInput() {
    setTimeout(() => {
      const partNumberRow = document.getElementById(this.partNumberInputId);
      if (partNumberRow) {
        partNumberRow.focus();
      }
    }, 100);
  }

  /**
   * Deletes the tool
   * @param tool - The tool to delete
   * @param event - The click event
   */
  deleteTool(tool: Product, event: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (!tool) {
      return;
    }

    tool.isLoading = true;
    tool.calculateTotals = !this.isLead;
    tool.deletionInProgress = true;
    const sortParams = this.isMobile ? null : this.sort;
    this.quoteToolsService.deleteToolWithQueueRequest(tool, this.currentPage, sortParams);
  }

  /**
   * Shows the notifications on the tool only if it has one
   * @param tool - The tool
   * @param event - The click event
   */
  showNotifications(tool: Product, toolIndex: number, event: Event) {
    // a2a, b2b, c2c product descriptions
    this.tooltipNotificationComponent.showTooltipNotification(tool, toolIndex, this.templateValues);
    event.stopPropagation();
  }

  tooltipCallback(event: ProductNotificationEvent) {
    const toBeCalled = {
      remove: () => this.deleteTool(event.tool, null),
      ignore: () => this.quoteToolsService.ignoreNotification(event),
      supersede: () => this.quoteToolsService.supersedeItem(event),
      oemSku: () => this.quoteToolsService.updateOemSkuProduct(event),
      edit: () => this.openCustomTool(event.tool, true),
      update: () => this.quoteToolsService.updateQuantity(event)
    };

    return toBeCalled[event.button.callbackType]();
  }

  /**
   * Opens the notes dialog
   * @param tool - The tool
   * @param event - Te click event
   */
  openNotesDialog(tool: Product, event: Event) {
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['ds', 'fullWidth'];
    dialogConfig.width = this.isMobile ? '80vw' : '';
    dialogConfig.data = tool.lineNotes || tool || { notes: '' };
    dialogConfig.data.readOnly = !this.canEdit || this.quoteSaveInProgress;
    dialogConfig.data.disableSaveButton = !this.canEdit || this.quoteSaveInProgress;
    const dialogRef = this.dialog.open(ToolNotesDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(resp => {
      if (!resp || resp.index === 0) {
        return;
      } else if (resp.index === 1) {
        tool.notes = resp.data;
        if (tool.id) {
          this.updateTool(tool);
        } else {
          this.newProductRow.patchValue({ notes: resp.data });
          tool.lineNotes = resp.data ? { notes: resp.data, bomNotes: '' } : null;
        }
      }
    });
  }

  /**
   * Handles row click to enable editing
   * @param row - The row that was clicked
   * @param event - The click event
   * @param rowIndex - The index of the row
   */
  onRowClick(row: Product, event: Event, rowIndex: number) {
    event.stopPropagation();
    const target = event.target as HTMLElement;

    // Don't edit if clicking on action buttons, checkboxes, or links
    if (target.closest('.actions-cell') ||
      target.closest('ds-checkbox') ||
      target.closest('ds-button') ||
      target.closest('button') ||
      target.tagName === 'A' ||
      !this.canEdit ||
      this.quoteSaveInProgress ||
      this.itemizeInProgress ||
      row.isLoading) {
      return;
    }

    if (rowIndex === 0) {
      return;
    }

    if (this.editableRow === row && row.editable) {
      return;
    }

    if (this.editableRow && this.editableRow !== row) {
      // Don't start editing new row if save is in progress
      if (this.isSavingRow) {
        return;
      }
      this.saveEditedRow();
      // Don't start editing the new row if saveEditedRow is still processing (dialog open)
      if (this.isSavingRow) {
        return;
      }
    }

    this.startEditingRow(row);
  }

  /**
   * Starts editing a row
   * @param row - The row to be edited
   */
  private startEditingRow(row: Product) {
    row.editable = true;
    this.editableRow = row;
    this.oldRowData = Product.clone(row);

    this.currentRow.patchValue({
      lineNumber: row.lineNumber,
      customTool: row.customTool,
      productNumber: row.productNumber,
      description: row.description,
      quantity: row.quantity,
      netPrice: row.netPrice
    });

    this.quoteToolsService.updateItemInProgress$.next(true);
    this.startItemEditInProgress = true;
  }

  /**
   * Open confirmation dialog when net price has changed
   * @param callback callback
   */
  netPriceHasChanged(callback) {
    const confirm = () => callback(0);
    const cancel = () => callback(1);

    // The pop-up price approval is available only for USA and NS
    if (this.countryCode !== CountryCode.US && this.countryCode !== CountryCode.NS) {
      confirm();
      return;
    }

    this.dialogTitle = this.appStrings.product_netPriceConfirmTitle;
    this.dialogText = this.appStrings.product_netPriceMessage;
    this.dialogDisableClose = true;
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: confirm.bind(this) },
      { label: 'No', type: 'secondary', onclick: cancel.bind(this) },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }

  private stopItemEditProgress() {
    this.quoteToolsService.updateItemInProgress$.next(false);
    this.startItemEditInProgress = false;
  }

  /**
   * Triggers saving of the edited row
   */
  saveEditedRow() {
    if (!this.editableRow || this.isSavingRow) {
      return;
    }

    this.isSavingRow = true;
    const formValue = this.currentRow.value;
    this.editableRow.lineNumber = formValue.lineNumber;
    this.editableRow.customTool = formValue.customTool;
    this.editableRow.productNumber = formValue.productNumber;
    this.editableRow.description = formValue.description;
    this.editableRow.quantity = parseInt(formValue.quantity, 10);
    this.editableRow.netPrice = parseFloat(formValue.netPrice);

    if (this.editableRow.updateCustomToolInProgress) {
      this.editableRow.editable = false;
      this.editableRow = null;
      this.isSavingRow = false;
      return;
    }

    if (this.editableRow.deletionInProgress) {
      this.editableRow.editable = false;
      this.editableRow = null;
      this.isSavingRow = false;
      return;
    }

    if (!this.editableRow.productNumber || !this.editableRow.quantity) {
      this.notificationService.showNotification(this.appStrings.products_gridRequiredFieldsError, NotificationType.Error);
      this.stopItemEditProgress();
      this.editableRow.productNumber = this.editableRow.productNumber || this.oldRowData.productNumber;
      this.editableRow.quantity = this.editableRow.quantity || this.oldRowData.quantity;
      this.editableRow.editable = false;
      this.editableRow = null;
      this.isSavingRow = false;
      return;
    }

    if (this.editableRow.productNumber.indexOf('%') > -1) {
      this.notificationService.showNotification(this.appStrings.products_gridInvalidPartNumber, NotificationType.Error, true);
      this.stopItemEditProgress();
      this.editableRow.productNumber = this.oldRowData.productNumber;
      this.editableRow.editable = false;
      this.editableRow = null;
      this.isSavingRow = false;
      return;
    }

    const currentNetPrice = this.editableRow.netPrice || null;
    const oldNetPrice = this.oldRowData.netPrice || null;
    const netPriceChanged = currentNetPrice !== oldNetPrice && !(currentNetPrice === null && oldNetPrice === null);

    if (netPriceChanged) {
      const rowToUpdate = this.editableRow;
      this.netPriceHasChanged((result: number) => this.netPriceHasChangedCallback(result, rowToUpdate));
      return;
    }

    this.editableRow.editable = false;

    if (!this.editableRow.netPrice) {
      delete this.editableRow.netPrice;
    }

    if (!Product.equals(this.oldRowData, this.editableRow)) {
      this.productDataChanged();
    }

    this.stopItemEditProgress();
    this.editableRow = null;
    this.isSavingRow = false;
  }

  /**
   * Updating or reverting rows data based on net price has changed
   * @param result confirmation popup result
   */
  netPriceHasChangedCallback(result: number, row: Product) {
    if (result === 0) {
      // yes button pressed
      if (row?.editable) {
        row.editable = false;
      }
      this.stopItemEditProgress();
      this.updateTool(row);
      this.oldRowData = Product.clone(row);
      this.editableRow = null;
    }

    if (result === 1) {
      // no button pressed - revert only net price but keep row editable
      row.netPrice = this.oldRowData.netPrice;
      this.currentRow.patchValue({
        netPrice: this.oldRowData.netPrice
      });
    }

    this.isSavingRow = false;
  }

  /**
   * Updates a tool when a tool has been changed
   */
  productDataChanged() {
    if (this.editableRow.lineNumber !== this.oldRowData.lineNumber) {
      if (!this.editableRow.lineNumber || this.editableRow.lineNumber > this.totalRecords + 1) {
        this.editableRow.lineNumber = this.oldRowData.lineNumber;
      }
      this.updateTool(this.editableRow, this.currentPage);
    } else {
      this.updateTool(this.editableRow);
    }
  }

  /**
   * Updates the tool
   * @param tool - The tool to be updated
   * @param currentPage - The current page by which to get the products if reorder by line number
   */
  updateTool(tool: Product, currentPage?: number) {
    const oldToolPrice = tool.netPrice || 0;
    // the net price is either > 0 or null.
    if (tool.hasOwnProperty('netPrice') && (tool.netPrice === 0 || tool.netPrice === null)) {
      delete tool.netPrice;
    }
    tool.calculateTotals = !this.isLead;
    tool.isLoading = true;
    const comparingRow = this.oldRowData || Product.clone(tool);
    const oldDescription = tool.description;
    this.quoteToolsService.editTool(tool, currentPage, () => this.isLoading = false, (newToolData: Product) => {
      this.isLoading = false;

      // The net price change api will be called only when the price or the price and part number is changed
      if ((comparingRow.netPrice !== oldToolPrice && this.countryCode === CountryCode.US &&
        newToolData.productNumber === comparingRow.productNumber) ||
        ((newToolData.dbNetPrice !== newToolData.netPrice && comparingRow.netPrice !== oldToolPrice &&
          this.countryCode === CountryCode.US && newToolData.productNumber !== comparingRow.productNumber) &&
          (newToolData.quantity === comparingRow.quantity)
          && (oldDescription === comparingRow.description) && (newToolData.notes === comparingRow.notes))) {
        this.quoteToolsService.sendNetPriceChanges(
          this.quoteNumber, newToolData.dbNetPrice, newToolData.netPrice, newToolData.productNumber).subscribe();
      }

      if (
        this.checkboxForm.controls.atpChecked &&
        this.isOnline &&
        (comparingRow.productNumber !== newToolData.productNumber ||
          comparingRow.quantity !== newToolData.quantity)
      ) {
        this.quoteToolsService.refreshProductAtp(newToolData.id);
      }
    });
    this.stopItemEditProgress();
  }

  /**
   * Sorts table data
   * @param sort - The sort
   */
  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      this.quoteToolsService.getProductList(this.currentPage);
      return;
    }

    this.currentPage = 0; // reset to first page
    this.quoteToolsService.getProductList(this.currentPage, SortColumn[sort.active], SortDirection[sort.direction]);
  }

  /**
   * Downloads the Excel template file
   */
  downloadExcelTemplate() {
    this.quoteToolsService.downloadExcelTemplate();
  }

  /**
   * Reorder the table on the server side
   */
  renumberItemList() {
    if (this.isMobile || !this.sort.active || !this.sort.direction) {
      return;
    }

    const confirm = () => {
      this.quoteToolsService.reorderProducts(
        this.currentPage,
        +SortColumn[this.sort.active],
        +SortDirection[this.sort.direction]
      );
    };

    this.dialogTitle = this.appStrings.button_renumberItemList;
    this.dialogText = this.appStrings.quotes_renumber;
    this.dialogDisableClose = false;
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: confirm.bind(this) },
      { label: 'No', type: 'secondary' },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }

  /**
   * Display atp dialog box
   */
  openATPPop(tool: Product, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    this.dialog.open(QuoteAtpComponent, {
      width: this.isMobile ? '' : this.fitContentSize,
      height: this.isMobile ? '' : this.fitContentSize,
      panelClass: this.isMobile ? ['ds', 'fullWidth', 'fullScreen'] : this.panelClass,
      data: {
        partNumber: tool.productNumber
      }
    });
    if (!this.isLead) {
      this.quoteToolsService.checkAtpBackorderNotification(tool.id, this.customerId)
        .subscribe(newNotifications => {
          tool.notifications = newNotifications;
          this.quoteToolsService.setNotificationIcon(tool);
        });
    }
  }

  /**
   * Triggers file upload
   */
  triggerUpload() {
    this.fileUploadControl.nativeElement.click();
  }

  /**
   * opens the cosigned warehouse popup
   */
  showConsignedWarehousePopup() {
    this.dialog.open(ConsignedWarehouseComponent, {
      panelClass: this.isMobile ? ['ds', 'fullWidth', 'fullScreen'] : ['ds', 'fullWidth'],
      data: {
        quoteDetails: this.quoteDetails
      }
    });
  }

  /**
   * Uploads the excel file
   * @param event - The upload event
   * @param notification - SignalR notification object
   */
  doExcelUpload(fileList: FileList, eventTarget: any, notification: SignalrNotification) {
    if (fileList.length > 0) {
      this.quoteToolsService.uploadExcel(fileList, notification, error => this.afterUpload(error, eventTarget));
    } else {
      this.afterUpload({}, eventTarget);
    }
  }

  uploadExcel(event: any) {
    const files: FileList = event.target.files;
    const target = event.target;
    this.isUploading = true;

    this.signalrService.startConnection(SignalrConnectionType.SaveUploadedProducts)
      .then(() => {
        const signalrNotification = new SignalrNotification(
          this.signalrService.connectionId,
          null,
          SignalrMethodName.saveUploadedProducts
        );
        this.doExcelUpload(files, target, signalrNotification);
      }).catch(err => this.afterUpload(err, target));
  }

  /**
   * Resets the file upload and reloads list
   */
  afterUpload(error: any, target: any) {
    if (target) {
      target.value = null; // reset excel file input
    }
    if (error) {
      this.isUploading = false;
    }
  }

  /**
   * Returns products details required by parent component
   */
  getProductsDetails() {
    return {
      numberOfTools: this.totalRecords
    };
  }

  /**
   * Makes validation in inputs to not allow value of zero
   * @param as event
   */
  validateForZero(event) {
    if (event.target.value === '0') {
      event.target.value = '';
    }
  }

  private actionAfterProductsUploaded() {
    this.isUploading = false;
    this.signalrService.closeConnection();
    this.quoteToolsService.productListHasChanges$.next(true);
    this.notificationService.showNotification('Products uploaded successfully', NotificationType.Success);

    // reset sort or update data
    if (!this.isMobile && (this.sort.active || this.sort.direction)) {
      this.sort.sort({ id: '', start: 'asc', disableClear: false });
      this.sortData({ active: '', direction: '' });
    } else {
      this.quoteToolsService.getProductList(this.currentPage);
    }
  }

  private setUploadedProductsProgress(progress: SignalRUpload) {
    if (progress.state === SignalrProgressState.Done) {
      this.actionAfterProductsUploaded();
    }
    if (progress.state === SignalrProgressState.Error) {
      this.notificationService.showNotification(progress.message, NotificationType.Error);
      this.signalrService.closeConnection();
      this.isUploading = false;
    }
  }

  /**
   * Copying quote details for import
   */
  copyQuoteDetailsForImport() {
    let quoteDestination = this.route.snapshot.paramMap.get('quoteId');
    if (!quoteDestination) {
      quoteDestination = this.quoteToolsService.getQuoteIdForNewQuotes();
    }
    this.importItemsQuoteService.setDestinationQuoteId(quoteDestination);
    this.router.navigate(['quotes']);
  }

  /**
   * Edits an item in mobile view
   * @param item - The item to edit
   * @param event - Click event
   */
  addItem() {
    const dialogRef = this.dialog.open(AddProductComponent, {
      panelClass: ['ds', 'fullWidth', 'fullScreen'],
      data: { productInfo: new Product() }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.buttonIndex === 1) {
        this.addNewTool(result.data);
      }
    });
  }

  /**
   * Edits an item in mobile view
   * @param item - The item to edit
   * @param event - Click event
   */
  editItem(item: Product, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    const dialogRef = this.dialog.open(AddProductComponent, {
      panelClass: ['ds', 'fullWidth', 'fullScreen'],
      data: { productInfo: item }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.buttonIndex === 1) {
        item.isLoading = true;
        const editedItem = result.data;
        editedItem.lineNotes = { notes: editedItem.notes };
        this.updateTool(editedItem, this.currentPage);
      }
    });
  }

  /**
   * Opens custom tool dialog
   * @param element tool
   */
  openCustomTool(item: Product, fromNotification = false) {
    item.updateCustomToolInProgress = true;
    const isChecked = fromNotification || !item.customTool;
    if (isChecked) {
      this.customToolCheckedPopup(item, fromNotification);
    } else {
      this.customToolNotCheckedPopup(item);
    }
  }

  continue() {
    if (this.isLead) {
      this.goToPaymentDetails.emit();
    } else {
      this.goToOrderNotifications.emit();
    }
  }

  /**
   * Handles the value change of the ATP toggle
   */
  toggleAtp() {
    const value = this.checkboxForm.controls.atpChecked.value;
    if (!value) {
      this.quoteToolsService.updateATPFlag(this.customerId, this.leadId, value);
      return;
    }

    const confirmATPChange = () => {
      this.quoteToolsService.atpRefresh$.next(undefined);
      this.quoteToolsService.updateATPFlag(this.customerId, this.leadId, value);
    };

    const revertATPChange = () => {
      this.checkboxForm.controls.atpChecked.setValue(false);
    };

    this.dialogTitle = this.appStrings.quote_detailsConfirmTitle;
    this.dialogText = this.appStrings.quoteDetails_atpMessage;
    this.dialogDisableClose = true;
    this.dialogButtons = [
      { label: 'Yes', type: 'primary', onclick: confirmATPChange.bind(this) },
      { label: 'No', type: 'secondary', onclick: revertATPChange.bind(this) },
    ];

    this.cdRef.detectChanges();
    this.dialogOpen$.emit(true);
  }

  /**
   * Returns sorting parameters, null for mobile devices
   */
  private getSortParams() {
    if (this.isMobile) {
      return { sortColumn: null, sortDirection: null };
    }
    return {
      sortColumn: SortColumn[this.sort?.active],
      sortDirection: SortDirection[this.sort?.direction]
    };
  }

  /**
   * Adds a new tool from row 0
   */
  addNewToolFromRow() {
    const formValue = this.newProductRow.value;
    const tool = new Product();
    tool.productNumber = formValue.productNumber;
    tool.description = formValue.description;
    tool.quantity = parseInt(formValue.quantity, 10) || 1;
    tool.netPrice = parseFloat(formValue.netPrice);
    tool.notes = formValue.notes;

    this.addNewTool(tool);

    if (this.currentProductList && this.currentProductList.length > 0 && this.currentProductList[0]) {
      this.currentProductList[0].notes = '';
      this.currentProductList[0].lineNotes = null;
    }

    this.newProductRow.reset({
      productNumber: '',
      description: '',
      quantity: 1,
      netPrice: '',
      notes: '',
    });
  }

  /**
   * Handles Enter key press on table inputs
   */
  onInputKeyDown(event: KeyboardEvent, isNewRow: boolean) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isNewRow && !this.disableExcelUpload && !this.quoteSaveInProgress) {
        this.addNewToolFromRow();
      } else if (this.editableRow) {
        this.saveEditedRow();
      }
    }
  }
}

